import { useState, useEffect } from "react";
import { Loader2, User, Save, KeyRound } from "lucide-react";
import toast from "react-hot-toast";
import driverApi from "../../utils/driverApi";
import { useDriverProfile } from "../../hooks/useDriverPortal";

const inputStyle = {
  width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px",
  background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8, fontSize: 13.5, color: "#111827", outline: "none",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6, display: "block" };
const readOnlyStyle = { ...inputStyle, background: "#F3F4F6", color: "#9CA3AF" };

export default function DriverProfile() {
  const { profile, loading, updateProfile } = useDriverProfile();
  const [phone,                  setPhone]                  = useState("");
  const [emergencyContactName,   setEmergencyContactName]   = useState("");
  const [emergencyContactPhone,  setEmergencyContactPhone]  = useState("");
  const [emergencyRelation,      setEmergencyRelation]      = useState("");
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword,     setNewPassword]     = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setPhone(profile.phone || "");
    setEmergencyContactName(profile.emergencyContactName || "");
    setEmergencyContactPhone(profile.emergencyContactPhone || "");
    setEmergencyRelation(profile.emergencyRelation || "");
  }, [profile]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ phone, emergencyContactName, emergencyContactPhone, emergencyRelation });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters");
    setChangingPassword(true);
    try {
      await driverApi.post("/api/driver-auth/change-password", { currentPassword, newPassword });
      toast.success("Password changed");
      setCurrentPassword(""); setNewPassword("");
    } catch (err) {
      toast.error(err.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  }

  if (loading || !profile) {
    return (
      <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loader2 size={18} style={{ animation: "spin 1s linear infinite", color: "#9CA3AF" }} />
      </main>
    );
  }

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 2, display: "flex", alignItems: "center", gap: 8 }}>
          <User size={18} color="#4F46E5" /> Profile
        </div>
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>You can edit your phone, emergency contact, and password. Everything else is managed by your fleet owner.</div>

        <form onSubmit={handleSave} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}><label style={labelStyle}>Name</label><input style={readOnlyStyle} value={profile.name} disabled /></div>
            <div style={{ flex: 1 }}><label style={labelStyle}>Employee ID</label><input style={readOnlyStyle} value={profile.employeeId || "—"} disabled /></div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}><label style={labelStyle}>License Number</label><input style={readOnlyStyle} value={profile.licenseNumber || "—"} disabled /></div>
            <div style={{ flex: 1 }}><label style={labelStyle}>Assigned Vehicle</label><input style={readOnlyStyle} value={profile.assignedVehicle?.plateNumber || "None"} disabled /></div>
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input style={inputStyle} value={phone} onChange={e => setPhone(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}><label style={labelStyle}>Emergency Contact Name</label><input style={inputStyle} value={emergencyContactName} onChange={e => setEmergencyContactName(e.target.value)} /></div>
            <div style={{ flex: 1 }}><label style={labelStyle}>Emergency Contact Phone</label><input style={inputStyle} value={emergencyContactPhone} onChange={e => setEmergencyContactPhone(e.target.value)} /></div>
          </div>
          <div><label style={labelStyle}>Emergency Relation</label><input style={inputStyle} value={emergencyRelation} onChange={e => setEmergencyRelation(e.target.value)} /></div>

          <button type="submit" disabled={saving} style={{ height: 40, borderRadius: 9, border: "none", background: saving ? "#9CA3AF" : "#4F46E5", color: "#fff", fontWeight: 700, fontSize: 13.5, cursor: saving ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {saving ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={14} />} Save Changes
          </button>
        </form>

        <form onSubmit={handleChangePassword} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}><KeyRound size={15} color="#4F46E5" /> Change Password</div>
          <input type="password" placeholder="Current password" style={inputStyle} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
          <input type="password" placeholder="New password (min 6 characters)" style={inputStyle} value={newPassword} onChange={e => setNewPassword(e.target.value)} />
          <button type="submit" disabled={changingPassword} style={{ height: 40, borderRadius: 9, border: "1px solid #E5E7EB", background: "#fff", color: "#4F46E5", fontWeight: 700, fontSize: 13.5, cursor: changingPassword ? "default" : "pointer" }}>
            {changingPassword ? "Changing…" : "Change Password"}
          </button>
        </form>
      </div>
    </main>
  );
}
